import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Typography, Grid, ListItemText, ListItem, List, CircularProgress } from '@mui/material';
import { StyledBottomButton, StyledHeadingGrid, StyledGridContainer, StyledPaper } from './styles';

//Components
import RouterBreadcrumbs from "../../components/Breadcrumbs/RouterBreadcrumbs";
import Title from "../../components/Typography/Title";

//custom hook
import { useFetch } from "../../hooks/useFetch";

//constants
import { API_URLS } from "../../Constants";

//utils
import { getFormattedPrevYrRank } from '../../utils/getFormattedPrevYrRank';
import { getImpactAreaForRoadSegment } from '../../utils/getImpactAreaForRoadSegment';

//Breadcrumb path items to view roadway.
const roadwayPathItems = [
    "Dashboard",
    "Roadways",
    "View Roadway"
]

//Invoked for viewing a roadway.
export default function ViewSelectedRoadway() {
    const navigate = useNavigate();
    const location = useLocation();
    const { segmentId } = location.state || {};
    const fetcher = useFetch("get", `${API_URLS["API_GET_ROADWAY_BY_ID"]}?id=${segmentId}`);
    const [previousRanks, setPreviousRanks] = useState([]);

    //React hooks
    const [roadway, setRoadway] = useState([]);

    //Fetch data from API when the component renders.
    useEffect(() => {

        //If no segment id is found, navigate to view projects page.
        if (!segmentId) {
            navigate('/view-roadways');
            return;
        }
        fetcher.setRequest({});
    }, []);

    //Once response is received, set appropriate state variables.
    useEffect(() => {
        const response = fetcher.serverResponse;
        if (response?.success && response?.data?.length > 0) {
            setRoadway(response.data);
            const formattedPrevYrRanks = getFormattedPrevYrRank(response.data[0].previousRanks);
            const latestTenRanks = formattedPrevYrRanks.sort((a, b) => a.year - b.year).slice(-10);
            setPreviousRanks(latestTenRanks);
        }
    }, [fetcher.serverResponse]);

    return (
        <Grid container spacing={2}>

            {/* Breadcrumbs for the page */}
            <Grid item xs={12} md={12}>
                <RouterBreadcrumbs pathItems={roadwayPathItems} />
            </Grid>

            {/* Page Title for the page */}
            <Grid item xs={12} md={12}>
                <Title title="View Roadway" />
            </Grid>

            {fetcher.isLoading ? (
                <Grid container justifyContent="center" alignItems="center" style={{ height: '80vh' }}>
                    <CircularProgress />
                </Grid>
            ) : roadway.length > 0 && (
                <>
                    {/* Roadway Information Heading */}
                    < StyledHeadingGrid item xs={12} sx={{ marginTop: "0px" }}>
                        <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold' }}>
                            Roadway Information
                        </Typography>
                    </StyledHeadingGrid>

                    {/* Display Roadway Information */}
                    <Grid item xs={12} md={12}>
                        <StyledPaper elevation={3}>
                            <Grid container spacing={2}>
                                <Grid item xs={4} md={4}>
                                    <List role="presentation">
                                        <ListItem role="presentation">
                                            <ListItemText
                                                primary={<b>TTI Segment ID</b>}
                                                secondary={roadway[0].segmentId || "None"}
                                            />
                                        </ListItem>
                                        <ListItem role="presentation">
                                            <ListItemText
                                                primary={<b>Rank</b>}
                                                secondary={roadway[0].rank || "None"}
                                            />
                                        </ListItem>
                                        <ListItem role="presentation">
                                            <ListItemText
                                                primary={<b>Number of Projects</b>}
                                                secondary={roadway[0].numOfProjects || "0"}
                                            />
                                        </ListItem>
                                    </List>
                                </Grid>
                                <Grid item xs={4} md={4}>
                                    <List role="presentation">
                                        <ListItem role="presentation">
                                            <ListItemText
                                                primary={<b>Annual Hours of Delay Per Mile</b>}
                                                secondary={roadway[0].annualDelayMile || "None"}
                                            />
                                        </ListItem>
                                        {roadway[0].annualCongestionCost && (
                                            <ListItem role="presentation">
                                                <ListItemText
                                                    primary={<b>Annual Congestion Cost in $</b>}
                                                    secondary={roadway[0].annualCongestionCost || "None"}
                                                />
                                            </ListItem>
                                        )}
                                    </List>
                                </Grid>
                                <Grid item xs={4} md={4}>
                                    <List role="presentation">
                                        <ListItem role="presentation">
                                            <ListItemText
                                                primary={<b>Year Plan Active</b>}
                                                secondary={roadway[0].yearPlanActive || "None"}
                                            />
                                        </ListItem>
                                        <ListItem role="presentation">
                                            <ListItemText
                                                primary={<b>Quarter Plan Active</b>}
                                                secondary={roadway[0].quarterPlanActive || "None"}
                                            />
                                        </ListItem>
                                    </List>
                                </Grid>
                            </Grid>
                        </StyledPaper>
                    </Grid>

                    {/* Roadway Specifics Heading */}
                    <StyledHeadingGrid item xs={12}>
                        <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold' }}>
                            Roadway Specifics
                        </Typography>
                    </StyledHeadingGrid>

                    {/* Display Roadway Specifics */}
                    <Grid item xs={12} md={12}>
                        <StyledPaper elevation={3}>
                            <Grid container spacing={2}>
                                <Grid item xs={4} md={4}>
                                    <List role="presentation">
                                        <ListItem role="presentation">
                                            <ListItemText
                                                primary={<b>Roadway Title</b>}
                                                secondary={roadway[0].roadwayTitle || "None"}
                                            />
                                        </ListItem>
                                        <ListItem role="presentation">
                                            <ListItemText
                                                primary={<b>Roadway Name</b>}
                                                secondary={roadway[0].roadwayName || "None"}
                                            />
                                        </ListItem>
                                    </List>
                                </Grid>
                                <Grid item xs={4} md={4}>
                                    <List role="presentation">
                                        <ListItem role="presentation">
                                            <ListItemText
                                                primary={<b>From</b>}
                                                secondary={roadway[0].from || "None"}
                                            />
                                        </ListItem>
                                        <ListItem role="presentation">
                                            <ListItemText
                                                primary={<b>To</b>}
                                                secondary={roadway[0].to || "None"}
                                            />
                                        </ListItem>
                                    </List>
                                </Grid>
                                <Grid item xs={4} md={4}>
                                    <List role="presentation">
                                        <ListItem role="presentation">
                                            <ListItemText
                                                primary={<b>County</b>}
                                                secondary={roadway[0].county || "None"}
                                            />
                                        </ListItem>
                                        <ListItem role="presentation">
                                            <ListItemText
                                                primary={<b>Region(s)</b>}
                                                secondary={roadway[0].region && ('name' in roadway[0].region) ? roadway[0].region['name'] : "None"}
                                            />
                                        </ListItem>
                                    </List>
                                </Grid>
                            </Grid>
                        </StyledPaper>
                    </Grid>

                    {/* Previous Ranks Heading */}
                    <StyledHeadingGrid item xs={12}>
                        <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold' }}>
                            Previous Ranks
                        </Typography>
                    </StyledHeadingGrid>

                    {/* Display Previous Ranks */}
                    <Grid item xs={12} md={12}>
                        <StyledPaper elevation={3}>
                            {previousRanks.length === 0 && (
                                <StyledGridContainer container spacing={2}>
                                    <Grid item xs={12}>
                                        <Typography variant="body2">No data available.</Typography>
                                    </Grid>
                                </StyledGridContainer>
                            )}
                            {previousRanks?.length > 0 && (
                                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px' }} aria-label="Previous Ranks">
                                    <thead>
                                        <tr style={{ textAlign: 'left' }}>
                                            <th style={{ padding: '8px' }}>Year</th>
                                            {previousRanks.map((row, index) => (
                                                <th key={`year-head-${index}`} style={{ padding: '8px', fontWeight: 'bold', fontStyle: 'italic' }}>
                                                    {row.year}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td style={{ padding: '8px', fontWeight: 'bold' }}>Ranks</td>
                                            {previousRanks.map((row, index) => (
                                                <td key={`rank-data-${index}`} style={{ padding: '8px' }}>
                                                    {row.rank}
                                                </td>
                                            ))}
                                        </tr>
                                    </tbody>
                                </table>
                            )}
                        </StyledPaper>
                    </Grid>

                    {/* Connected Projects Heading */}
                    < StyledHeadingGrid item xs={12}>
                        <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold' }}>
                            Connected Projects
                        </Typography>
                    </StyledHeadingGrid>

                    {/* Display Connected Projects */}
                    <Grid item xs={12} md={12}>
                        <StyledPaper elevation={3}>
                            {roadway[0].connectedProjects && roadway[0].connectedProjects.length > 0 ? (
                                <>
                                    <StyledGridContainer container spacing={2} sx={{ marginBottom: "25px" }}>
                                        <Grid item xs={4} sx={{ marginTop: "20px", fontWeight: 'bold' }}>
                                            Project Title
                                        </Grid>
                                        <Grid item xs={4} sx={{ marginTop: "20px", fontWeight: 'bold' }}>
                                            Project Type
                                        </Grid>
                                        <Grid item xs={4} sx={{ marginTop: "20px", fontWeight: 'bold' }}>
                                            Impact Area
                                        </Grid>
                                    </StyledGridContainer>
                                    {roadway[0].connectedProjects.map((row, index) =>
                                        <StyledGridContainer container spacing={2} key={index} sx={{ paddingBottom: "25px" }}>
                                            <Grid item xs={4}>
                                                <Typography variant="body2">
                                                    {row.projectTitle || "None"}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <Typography variant="body2">
                                                    {row.projectType || "None"}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <Typography variant="body2">
                                                    {row.impactArea
                                                        ? getImpactAreaForRoadSegment(row.impactArea, roadway[0].segmentId)
                                                        : "None"}
                                                </Typography>
                                            </Grid>
                                        </StyledGridContainer>
                                    )}
                                </>
                            ) : (
                                <StyledGridContainer container spacing={2}>
                                    <Grid item xs={12}>
                                        <Typography variant="body2">No connected projects available.</Typography>
                                    </Grid>
                                </StyledGridContainer>
                            )}
                        </StyledPaper>
                    </Grid>

                    {/* Notes Heading */}
                    <StyledHeadingGrid item xs={12}>
                        <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold' }}>
                            Notes
                            <Typography style={{ fontSize: 12, fontStyle: 'italic' }}>These notes won't be published</Typography>
                        </Typography>
                    </StyledHeadingGrid>

                    {/* Display Notes */}
                    <Grid item xs={12} md={12}>
                        <StyledPaper elevation={3}>
                            <Grid item xs={12}>
                                <Typography variant="body1">
                                    {roadway[0].notes}
                                </Typography>
                            </Grid>
                        </StyledPaper>
                    </Grid>

                    {/* Display Buttons */}
                    <Grid item xs={12} style={{ display: "flex", justifyContent: "center" }}>
                        <StyledBottomButton variant="contained" color="primary" onClick={() => { navigate('/view-roadways') }}>
                            Back
                        </StyledBottomButton>
                    </Grid>
                </>
            )}
        </Grid >
    );
}